import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { Box, Breadcrumbs as MuiBreadcrumbs, styled, Tooltip, Typography } from '@mui/material';

import {
  QUERY_PARAM_EXCLUDE_TAGS,
  QUERY_PARAM_INCLUDE_TAGS,
  QUERY_PARAM_SEARCH
} from 'Courses/hooks/urlSyncUtils';
import { useAppSelector } from 'Store';

import type { BreadcrumbItem } from './breadcrumbSlice';
import { selectBreadcrumbs } from './breadcrumbSlice';

const StyledMuiBreadcrumbs = styled(MuiBreadcrumbs)`
  max-width: 100%;
  overflow: hidden;

  & .MuiBreadcrumbs-li {
    min-width: 0;
    overflow: hidden;

    &:not(:last-child) {
      flex-shrink: 1;
    }

    &:last-child {
      flex-shrink: 0;
    }
  }
`;

const BreadcrumbContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
`;

const BreadcrumbTitle = styled(Typography)<{ $isLast?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  ${({ $isLast }) => $isLast && 'font-weight: 700 !important;'}
`;

const BreadcrumbStatistics = styled(Typography)<{ $isLast?: boolean }>`
  flex-shrink: 0;
  white-space: nowrap;
  ${({ $isLast }) => $isLast && 'font-weight: 700 !important;'}
`;

const StyledLinkBox = styled(Link)`
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
  text-decoration: none;
  color: inherit;
`;

export const Breadcrumbs = () => {
  const { t } = useTranslation();
  const breadcrumbs = useAppSelector(selectBreadcrumbs) as BreadcrumbItem[];
  const [searchParams] = useSearchParams();

  const getBreadcrumbUrl = (breadcrumb: BreadcrumbItem) => {
    if (breadcrumb.to !== '/') {
      return breadcrumb.to;
    }

    const courseParams = new URLSearchParams();
    const q = searchParams.get(QUERY_PARAM_SEARCH);
    const includeTags = searchParams.get(QUERY_PARAM_INCLUDE_TAGS);
    const excludeTags = searchParams.get(QUERY_PARAM_EXCLUDE_TAGS);

    if (q) {
      courseParams.set(QUERY_PARAM_SEARCH, q);
    }
    if (includeTags) {
      courseParams.set(QUERY_PARAM_INCLUDE_TAGS, includeTags);
    }
    if (excludeTags) {
      courseParams.set(QUERY_PARAM_EXCLUDE_TAGS, excludeTags);
    }

    const urlQuery = courseParams.toString();
    return urlQuery ? `${breadcrumb.to}?${urlQuery}` : breadcrumb.to;
  };

  const parseBreadcrumbLabel = (label: string) => {
    const statisticsPattern = /^(.+?)\s*\((\d+\/\d+\/\d+)\)$/;
    const match = statisticsPattern.exec(label);

    if (match) {
      return {
        title: match[1],
        statistics: match[2],
        hasStatistics: true
      };
    }

    return { title: label, statistics: '', hasStatistics: false };
  };

  return (
    <StyledMuiBreadcrumbs>
      {breadcrumbs.map((breadcrumb, index) => {
        const breadcrumbText =
          breadcrumb.label ?? (breadcrumb.translateKey ? t(breadcrumb.translateKey) : '');
        const breadcrumbKey = `${breadcrumb.to}-${breadcrumb.label ?? breadcrumb.translateKey ?? ''}`;
        const isLastBreadcrumb = breadcrumbs.length - 1 === index;
        const { title, statistics, hasStatistics } = parseBreadcrumbLabel(breadcrumbText);
        const hasLabel = Boolean(breadcrumb.label);
        const isCourseTitle = hasLabel;

        const titleElement = <BreadcrumbTitle $isLast={isLastBreadcrumb}>{title}</BreadcrumbTitle>;

        const titleWithTooltip = isCourseTitle ? (
          <Tooltip title={title} arrow>
            {titleElement}
          </Tooltip>
        ) : (
          titleElement
        );

        const content = (
          <BreadcrumbContainer>
            {titleWithTooltip}
            {hasStatistics && (
              <BreadcrumbStatistics
                $isLast={isLastBreadcrumb}
              >{`(${statistics})`}</BreadcrumbStatistics>
            )}
          </BreadcrumbContainer>
        );

        if (isLastBreadcrumb) {
          return <div key={breadcrumbKey}>{content}</div>;
        }

        return (
          <Link key={breadcrumbKey} to={getBreadcrumbUrl(breadcrumb)}>
            {content}
          </Link>
        );
      })}
    </StyledMuiBreadcrumbs>
  );
};
